/**
 * YB-2327 — PAPI refuses an authorized pass whose dates overlap one the same person already holds
 * with the same role in the event, with `errorCode: authorized_pass_overlaps` and
 * `overlapping_passes`. The portal builds its message from those — never from `errorText`, which is
 * PAPI's English sentence — so it reads in the user's language and names the holder, the role, the
 * existing pass and both date ranges.
 */

export const AUTHORIZED_PASS_OVERLAPS = 'authorized_pass_overlaps'

/** The GraphQL selection every authorized create / import operation asks for. */
export const OVERLAPPING_PASSES_FIELDS = `overlapping_passes { row holder_name holder_mobile client_role_id client_role_name existing_pass_id existing_row existing_start existing_end requested_start requested_end }`

export interface OverlappingPass {
  /** Excel row, 1 = the first row under the header; 1 on a single create. */
  row?: number | null
  holder_name?: string | null
  holder_mobile?: string | null
  client_role_id?: number | null
  client_role_name?: string | null
  /** The pass it overlaps; null when it overlaps an earlier row of the same file (`existing_row`). */
  existing_pass_id?: number | null
  existing_row?: number | null
  /** ISO instants. */
  existing_start: string
  existing_end: string
  requested_start: string
  requested_end: string
}

interface Translator {
  instant(key: string, params?: object): string
}

const KEY = 'AUTHORIZED.OVERLAP'

/** dd/MM/yyyy HH:mm in Israel — the event's local time. */
export function formatOverlapDate(iso: string): string {
  const date = new Date(iso)
  if (isNaN(date.getTime())) return iso
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Jerusalem',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`
}

/** "052-3400002": PAPI stores a 9-digit local number without its leading zero. */
export function formatOverlapMobile(mobile: string | null | undefined): string {
  const digits = String(mobile ?? '').replace(/\D/g, '')
  if (!digits) return ''
  const local = digits.length === 9 && !digits.startsWith('0') ? `0${digits}` : digits
  return local.length === 10 ? `${local.slice(0, 3)}-${local.slice(3)}` : local
}

/** The holder, the role, the existing pass and both date ranges, localised. */
export function describeOverlappingPass(translate: Translator, item: OverlappingPass): string {
  const rolePhrase = item.client_role_name ? translate.instant(`${KEY}.ROLE`, { role: item.client_role_name }) : translate.instant(`${KEY}.SAME_ROLE`)
  const params = {
    holder: item.holder_name?.trim() || '',
    mobile: formatOverlapMobile(item.holder_mobile),
    rolePhrase,
    passId: item.existing_pass_id ?? '',
    existingRow: item.existing_row ?? '',
    existingStart: formatOverlapDate(item.existing_start),
    existingEnd: formatOverlapDate(item.existing_end),
    requestedStart: formatOverlapDate(item.requested_start),
    requestedEnd: formatOverlapDate(item.requested_end),
  }
  return translate.instant(item.existing_pass_id != null ? `${KEY}.ITEM` : `${KEY}.ITEM_IN_FILE`, params)
}

/**
 * The full message for a refused single create, or the rows of a bulk create: that the pass was not
 * created, why, and what to do instead.
 */
export function buildAuthorizedOverlapMessage(translate: Translator, list: OverlappingPass[] | null | undefined): { summary: string; detail: string } {
  const items = [...(list ?? [])].sort((a, b) => (a.row ?? 0) - (b.row ?? 0))
  const several = items.length > 1
  const blocks = items.map((item) => {
    const text = describeOverlappingPass(translate, item)
    return several ? `${translate.instant(`${KEY}.ROW`, { row: item.row ?? '', holder: item.holder_name || formatOverlapMobile(item.holder_mobile) })}\n${text}` : text
  })
  return {
    summary: translate.instant(several ? `${KEY}.ROWS_TITLE` : `${KEY}.TITLE`),
    detail: [...blocks, translate.instant(`${KEY}.INSTRUCTION`)].join('\n\n'),
  }
}
